import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Card, Field, FormGrid, SelectField } from '@crave/ui';
import { api } from '../api.ts';
import { Notice, Section, errorText } from '../components/ui.tsx';
import { STAFF_ME_KEY, useStaff } from '../lib/staff.tsx';

interface Address {
  line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}
interface Location {
  id: string;
  slug: string;
  name: string;
  status: 'active' | 'inactive';
  locationType: string;
  timezone: string;
  phone: string | null;
  address: Address | null;
  orderingEnabled: boolean;
  orderingPaused: boolean;
  posEnabled: boolean;
  blockers: { code: string; message: string }[];
}

const TYPES: [string, string][] = [
  ['MOBILE', 'Mobile (truck or trailer)'],
  ['RESTAURANT', 'Restaurant or storefront'],
  ['POPUP', 'Pop-up'],
  ['EVENT', 'Event'],
  ['COMMISSARY', 'Commissary kitchen'],
];
const TYPE_WORDS = Object.fromEntries(TYPES);
const ZONES: [string, string][] = [
  ['America/New_York', 'Eastern'],
  ['America/Chicago', 'Central'],
  ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific'],
];

const blankAddress = { line1: '', city: '', state: 'FL', postal_code: '' };
const addressText = (a: Address | null) =>
  a?.line1 ? `${a.line1}, ${a.city ?? ''}, ${a.state ?? ''} ${a.postal_code ?? ''}`.trim() : null;
const cleanAddress = (a: typeof blankAddress) =>
  a.line1.trim() ? { ...a, state: a.state.toUpperCase() } : null;

/** Every location: add one, change its details, turn the POS on or off, deactivate it (#56). */
export function Locations() {
  const staff = useStaff();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canCreate = staff.can('locations.create');
  const list = useQuery({
    queryKey: ['admin-locations'],
    queryFn: async () => (await api.get<{ items: Location[] }>('/admin/locations')).data.items,
    enabled: staff.can('locations.manage'),
  });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin-locations'] });
    void qc.invalidateQueries({ queryKey: [STAFF_ME_KEY] });
  };

  const status = useMutation({
    mutationFn: async (v: { loc: Location; active: boolean }) =>
      api.post(`/admin/locations/${v.loc.id}/status`, { active: v.active }),
    onSuccess: (_d, v) => {
      setMsg({
        kind: 'ok',
        text: v.active
          ? `${v.loc.name} is active again. Turn on online ordering when it's ready.`
          : `${v.loc.name} is deactivated. Its orders and reports stay.`,
      });
      refresh();
    },
    onError: (err) => setMsg({ kind: 'error', text: errorText(err) }),
  });
  const pos = useMutation({
    mutationFn: async (v: { loc: Location; on: boolean }) =>
      api.patch(`/admin/locations/${v.loc.id}`, { pos_enabled: v.on }),
    onSuccess: refresh,
    onError: (err) => setMsg({ kind: 'error', text: errorText(err) }),
  });

  if (!staff.can('locations.manage'))
    return (
      <Section title="Locations">
        <Notice>Owners and managers manage locations.</Notice>
      </Section>
    );

  const open = (loc: Location, to: string) => {
    staff.setLocation(loc.slug);
    void navigate(to);
  };
  const items = [...(list.data ?? [])].sort(
    (a, b) =>
      Number(b.status === 'active') - Number(a.status === 'active') || a.name.localeCompare(b.name),
  );

  return (
    <Section
      title="Locations"
      right={
        canCreate && !adding ? (
          <Button onClick={() => setAdding(true)}>Add location</Button>
        ) : undefined
      }
    >
      {list.isError && (
        <Notice kind="error">Could not load locations. {errorText(list.error, '')}</Notice>
      )}
      {msg && (
        <div role="status">
          <Notice kind={msg.kind}>{msg.text}</Notice>
        </div>
      )}
      {adding && (
        <AddLocation
          locations={list.data ?? []}
          onClose={() => setAdding(false)}
          onAdded={(name) => {
            setAdding(false);
            setMsg({
              kind: 'ok',
              text: `${name} is added with online ordering off. Set its tax rate, hours and prices in Menu, prices & hours.`,
            });
            refresh();
          }}
        />
      )}
      {items.map((loc) => {
        const active = loc.status === 'active';
        const ordering = !active
          ? 'Inactive'
          : loc.orderingEnabled
            ? loc.orderingPaused
              ? 'Ordering paused'
              : 'Taking orders'
            : 'Ordering off';
        return (
          <Card key={loc.id} style={{ display: 'grid', gap: 12, opacity: active ? 1 : 0.75 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 22, flex: 1, minWidth: 200 }}>{loc.name}</h2>
              <span
                className="crave-status"
                data-tone={
                  !active ? 'muted' : loc.orderingEnabled && !loc.orderingPaused ? 'ok' : 'warn'
                }
              >
                {ordering}
              </span>
            </div>
            <div style={{ color: 'var(--ink-soft)', display: 'grid', gap: 2 }}>
              <span>
                {TYPE_WORDS[loc.locationType] ?? loc.locationType} ·{' '}
                {ZONES.find(([z]) => z === loc.timezone)?.[1] ?? loc.timezone} time
              </span>
              {addressText(loc.address) && <span>{addressText(loc.address)}</span>}
              {loc.phone && <span>{loc.phone}</span>}
              {active && !loc.orderingEnabled && loc.blockers.length > 0 && (
                <span>
                  {loc.blockers.length === 1
                    ? '1 thing to set up before online ordering.'
                    : `${loc.blockers.length} things to set up before online ordering.`}
                </span>
              )}
            </div>

            {active && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 44 }}>
                <input
                  type="checkbox"
                  checked={loc.posEnabled}
                  disabled={pos.isPending}
                  onChange={(e) => pos.mutate({ loc, on: e.target.checked })}
                  style={{ width: 22, height: 22, accentColor: 'var(--crave-red)' }}
                />
                <span>
                  <strong>Walk-up orders in the Kitchen app</strong>
                  <span style={{ display: 'block', fontSize: 14, color: 'var(--ink-soft)' }}>
                    Shows New order on this location's kitchen devices.
                  </span>
                </span>
              </label>
            )}

            {editing === loc.id ? (
              <EditLocation
                loc={loc}
                onClose={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  setMsg({ kind: 'ok', text: `${loc.name} is updated.` });
                  refresh();
                }}
              />
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {active && (
                  <>
                    <Button variant="secondary" onClick={() => open(loc, '/readiness')}>
                      Hours, tax &amp; ordering
                    </Button>
                    {staff.can('pos.readers') && (
                      <Button variant="secondary" onClick={() => open(loc, '/settings/readers')}>
                        POS &amp; card readers
                      </Button>
                    )}
                  </>
                )}
                <Button variant="ghost" onClick={() => setEditing(loc.id)}>
                  Edit details
                </Button>
                {canCreate &&
                  (active ? (
                    <Button
                      variant="ghost"
                      disabled={status.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Deactivate ${loc.name}? Online ordering turns off and it disappears for staff and customers. Past orders and reports stay.`,
                          )
                        )
                          status.mutate({ loc, active: false });
                      }}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      disabled={status.isPending}
                      onClick={() => status.mutate({ loc, active: true })}
                    >
                      Reactivate
                    </Button>
                  ))}
              </div>
            )}
          </Card>
        );
      })}
    </Section>
  );
}

function AddressFields({
  value,
  onChange,
}: {
  value: typeof blankAddress;
  onChange: (v: typeof blankAddress) => void;
}) {
  return (
    <FormGrid min={170}>
      <Field
        label="Street"
        value={value.line1}
        onChange={(e) => onChange({ ...value, line1: e.target.value })}
        wide
      />
      <Field
        label="City"
        value={value.city}
        onChange={(e) => onChange({ ...value, city: e.target.value })}
      />
      <Field
        label="State"
        maxLength={2}
        value={value.state}
        onChange={(e) => onChange({ ...value, state: e.target.value.toUpperCase() })}
      />
      <Field
        label="ZIP"
        inputMode="numeric"
        value={value.postal_code}
        onChange={(e) => onChange({ ...value, postal_code: e.target.value })}
      />
    </FormGrid>
  );
}

function AddLocation({
  locations,
  onClose,
  onAdded,
}: {
  locations: Location[];
  onClose: () => void;
  onAdded: (name: string) => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState('MOBILE');
  const [zone, setZone] = useState('America/New_York');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState(blankAddress);
  const [copyFrom, setCopyFrom] = useState(locations.find((l) => l.status === 'active')?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: async () =>
      api.post('/admin/locations', {
        name: name.trim(),
        location_type: type,
        timezone: zone,
        phone: phone.trim() || null,
        address: cleanAddress(address),
        ...(copyFrom ? { copy_settings_from: copyFrom } : {}),
      }),
    onSuccess: () => onAdded(name.trim()),
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Card>
      <form
        style={{ display: 'grid', gap: 14 }}
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (!name.trim()) return setError('Give the location a name.');
          add.mutate();
        }}
      >
        <strong>New location</strong>
        <FormGrid min={220}>
          <Field
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Crave Pizza Lake Wales"
          />
          <SelectField
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            options={TYPES}
          />
          <SelectField
            label="Time zone"
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            options={ZONES}
          />
          <Field
            label="Phone"
            value={phone}
            inputMode="tel"
            onChange={(e) => setPhone(e.target.value)}
          />
        </FormGrid>
        <AddressFields value={address} onChange={setAddress} />
        <SelectField
          label="Start with the hours and settings of"
          value={copyFrom}
          onChange={(e) => setCopyFrom(e.target.value)}
          options={[
            ['', 'Nothing, start blank'],
            ...locations.map((l): [string, string] => [l.id, l.name]),
          ]}
          hint="Hours, pickup times, prep times and policies. The tax rate is never copied."
        />
        {error && (
          <div role="alert">
            <Notice kind="error">{error}</Notice>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="submit" disabled={add.isPending}>
            {add.isPending ? 'Adding…' : 'Add location'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

function EditLocation({
  loc,
  onClose,
  onSaved,
}: {
  loc: Location;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(loc.name);
  const [type, setType] = useState(loc.locationType);
  const [zone, setZone] = useState(loc.timezone);
  const [phone, setPhone] = useState(loc.phone ?? '');
  const [address, setAddress] = useState({ ...blankAddress, ...(loc.address ?? {}) });
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () =>
      api.patch(`/admin/locations/${loc.id}`, {
        name: name.trim(),
        location_type: type,
        timezone: zone,
        phone: phone.trim() || null,
        address: cleanAddress(address),
      }),
    onSuccess: onSaved,
    onError: (err) => setError(errorText(err)),
  });
  return (
    <form
      style={{ display: 'grid', gap: 14, borderTop: '1px solid var(--line)', paddingTop: 14 }}
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!name.trim()) return setError('Give the location a name.');
        save.mutate();
      }}
    >
      <FormGrid min={220}>
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <SelectField
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          options={TYPES}
        />
        <SelectField
          label="Time zone"
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          options={ZONES.some(([z]) => z === zone) ? ZONES : [[zone, zone], ...ZONES]}
        />
        <Field
          label="Phone"
          value={phone}
          inputMode="tel"
          onChange={(e) => setPhone(e.target.value)}
        />
      </FormGrid>
      <AddressFields value={address} onChange={setAddress} />
      {error && (
        <div role="alert">
          <Notice kind="error">{error}</Notice>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
